import { useState } from 'react';
import { format, subDays, startOfMonth } from 'date-fns';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

type ReportType = 'consumption' | 'drops' | 'waste';
type DateRange = '7d' | '30d' | 'mtd' | 'ytd' | 'custom';

interface ConsumptionReport {
  summary: {
    totalPartsConsumed: number;
    totalCost: number;
    uniquePartsUsed: number;
    avgDailyConsumption: number;
    topConsumedPart: string;
    periodDays: number;
  };
  byGroup: Array<{
    inventory_group: string;
    total_consumed: number;
    total_cost: number;
    items: Array<{
      part_number: string;
      description: string;
      quantity_consumed: number;
      unit_price: number;
      total_cost: number;
      movement_count: number;
    }>;
  }>;
  trends: Array<{ date: string; quantity: number; cost: number }>;
  topItems: Array<{
    part_number: string;
    description: string;
    quantity_consumed: number;
    total_cost: number;
  }>;
}

interface DropReport {
  summary: {
    totalDropsCreated: number;
    totalDropsUsed: number;
    totalDropsAvailable: number;
    totalDropsWasted: number;
    overallUtilizationRate: number;
    totalLengthSaved: number;
    estimatedSavings: number;
    wastePercentage: number;
  };
  byComponent: Array<{
    part_number: string;
    description: string;
    component_type: string;
    drops_created: number;
    drops_used: number;
    drops_available: number;
    drops_wasted: number;
    total_length_created: number;
    total_length_used: number;
    utilization_rate: number;
    estimated_savings: number;
  }>;
  recentDrops: Array<{
    id: string;
    part_number: string;
    length: number;
    status: string;
    created_at: string;
    order_number?: string;
  }>;
}

interface WasteReport {
  summary: {
    totalWasteEvents: number;
    totalWasteQuantity: number;
    totalWasteCost: number;
    avgWasteCostPerDay: number;
    topWastedPart: string;
    periodDays: number;
  };
  byComponentType: Array<{
    component_type: string;
    waste_count: number;
    total_quantity: number;
    total_cost: number;
    avg_cost_per_waste: number;
    items: Array<{
      part_number: string;
      description: string;
      quantity_wasted: number;
      total_cost: number;
      waste_reason?: string;
    }>;
  }>;
  trends: Array<{ date: string; quantity: number; cost: number }>;
  recentWaste: Array<{
    part_number: string;
    description: string;
    quantity_wasted: number;
    total_cost: number;
    waste_reason?: string;
    waste_date: string;
  }>;
}

export default function InventoryReportsPage() {
  const [reportType, setReportType] = useState<ReportType>('consumption');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ConsumptionReport | DropReport | WasteReport | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const getDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case '7d':
        return { start: subDays(now, 7), end: now };
      case '30d':
        return { start: subDays(now, 30), end: now };
      case 'mtd':
        return { start: startOfMonth(now), end: now };
      case 'ytd':
        return { start: new Date(now.getFullYear(), 0, 1), end: now };
      case 'custom':
        return {
          start: customStart ? new Date(customStart) : subDays(now, 30),
          end: customEnd ? new Date(customEnd) : now,
        };
      default:
        return { start: subDays(now, 30), end: now };
    }
  };

  const generateReport = async () => {
    const { start, end } = getDateRange();
    setLoading(true);
    setReportData(null);

    try {
      const startDate = format(start, 'yyyy-MM-dd');
      const endDate = format(end, 'yyyy-MM-dd');

      let data;
      switch (reportType) {
        case 'consumption':
          data = await api.reports.getInventoryConsumptionReport(startDate, endDate);
          break;
        case 'drops':
          data = await api.reports.getDropUtilizationReport(startDate, endDate);
          break;
        case 'waste':
          data = await api.reports.getWasteTrackingReport(startDate, endDate);
          break;
      }
      setReportData(data);
    } catch (error) {
      console.error('Failed to generate report:', error);
      // Set mock data for development/demo
      setReportData(getMockData(reportType));
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData) return;

    let csvContent = '';
    const { start, end } = getDateRange();
    const dateRangeStr = `${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`;

    if (reportType === 'consumption') {
      const data = reportData as ConsumptionReport;
      csvContent = `Inventory Consumption Report\nDate Range: ${dateRangeStr}\n\n`;
      csvContent += `Summary\n`;
      csvContent += `Total Parts Consumed,${data.summary.totalPartsConsumed}\n`;
      csvContent += `Total Cost,$${data.summary.totalCost}\n`;
      csvContent += `Unique Parts Used,${data.summary.uniquePartsUsed}\n`;
      csvContent += `Avg Daily Consumption,${data.summary.avgDailyConsumption}\n\n`;
      csvContent += `Part Number,Description,Quantity Consumed,Unit Price,Total Cost\n`;
      data.topItems.forEach(item => {
        csvContent += `"${item.part_number}","${item.description}",${item.quantity_consumed},$${item.unit_price || 0},$${item.total_cost}\n`;
      });
    } else if (reportType === 'drops') {
      const data = reportData as DropReport;
      csvContent = `Drop Utilization Report\nDate Range: ${dateRangeStr}\n\n`;
      csvContent += `Summary\n`;
      csvContent += `Total Drops Created,${data.summary.totalDropsCreated}\n`;
      csvContent += `Total Drops Used,${data.summary.totalDropsUsed}\n`;
      csvContent += `Utilization Rate,${data.summary.overallUtilizationRate}%\n`;
      csvContent += `Estimated Savings,$${data.summary.estimatedSavings}\n\n`;
      csvContent += `Part Number,Description,Drops Created,Drops Used,Utilization Rate,Savings\n`;
      data.byComponent.forEach(comp => {
        csvContent += `"${comp.part_number}","${comp.description}",${comp.drops_created},${comp.drops_used},${comp.utilization_rate}%,$${comp.estimated_savings}\n`;
      });
    } else {
      const data = reportData as WasteReport;
      csvContent = `Waste Tracking Report\nDate Range: ${dateRangeStr}\n\n`;
      csvContent += `Summary\n`;
      csvContent += `Total Waste Events,${data.summary.totalWasteEvents}\n`;
      csvContent += `Total Waste Quantity,${data.summary.totalWasteQuantity}\n`;
      csvContent += `Total Waste Cost,$${data.summary.totalWasteCost}\n\n`;
      csvContent += `Part Number,Description,Quantity Wasted,Total Cost,Reason\n`;
      data.recentWaste.forEach(item => {
        csvContent += `"${item.part_number}","${item.description}",${item.quantity_wasted},$${item.total_cost},"${item.waste_reason || ''}"\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inventory-${reportType}-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div>
      <PageHeader
        title="Inventory Reports"
        subtitle="Consumption, drop utilization, and waste tracking reports"
        actions={
          reportData && (
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
          )
        }
      />

      {/* Report Configuration */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value as ReportType);
                setReportData(null);
              }}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="consumption">Inventory Consumption</option>
              <option value="drops">Drop Utilization</option>
              <option value="waste">Waste Tracking</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="mtd">Month to Date</option>
              <option value="ytd">Year to Date</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          <div className="flex items-end">
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </Card>

      {/* Report Content */}
      {loading ? (
        <Card>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </Card>
      ) : reportData ? (
        <>
          {reportType === 'consumption' && <ConsumptionReportView data={reportData as ConsumptionReport} expandedGroups={expandedGroups} toggleGroup={toggleGroup} />}
          {reportType === 'drops' && <DropReportView data={reportData as DropReport} />}
          {reportType === 'waste' && <WasteReportView data={reportData as WasteReport} />}
        </>
      ) : (
        <Card>
          <div className="text-center py-12 text-gray-500">
            Select report options and click "Generate Report" to view data
          </div>
        </Card>
      )}
    </div>
  );
}

// Consumption Report Component
function ConsumptionReportView({
  data,
  expandedGroups,
  toggleGroup
}: {
  data: ConsumptionReport;
  expandedGroups: Set<string>;
  toggleGroup: (group: string) => void;
}) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-3xl font-bold text-gray-900">{data.summary.totalPartsConsumed.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Parts Consumed</p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-green-600">${data.summary.totalCost.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Total Cost</p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-blue-600">{data.summary.uniquePartsUsed}</p>
          <p className="text-sm text-gray-500">Unique Parts</p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-purple-600">{data.summary.avgDailyConsumption}</p>
          <p className="text-sm text-gray-500">Avg/Day</p>
        </Card>
      </div>

      {/* Top Consumed Parts */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Consumed Parts</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty Consumed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.topItems.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.part_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.description}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity_consumed.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">${item.total_cost.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* By Group */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Consumption by Inventory Group</h3>
        <div className="space-y-2">
          {data.byGroup.map((group, idx) => (
            <div key={idx} className="border rounded-lg">
              <button
                onClick={() => toggleGroup(group.inventory_group)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
              >
                <span className="font-medium text-gray-900">{group.inventory_group}</span>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">{group.total_consumed.toLocaleString()} units</span>
                  <span className="text-sm font-medium text-green-600">${group.total_cost.toLocaleString()}</span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedGroups.has(group.inventory_group) ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {expandedGroups.has(group.inventory_group) && (
                <div className="px-4 pb-3">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Part #</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {group.items.slice(0, 10).map((item, itemIdx) => (
                        <tr key={itemIdx}>
                          <td className="px-3 py-2 text-sm text-gray-900">{item.part_number}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{item.description}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-right">{item.quantity_consumed}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-right">${item.total_cost.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// Drop Utilization Report Component
function DropReportView({ data }: { data: DropReport }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-3xl font-bold text-gray-900">{data.summary.totalDropsCreated}</p>
          <p className="text-sm text-gray-500">Drops Created</p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-green-600">{data.summary.totalDropsUsed}</p>
          <p className="text-sm text-gray-500">Drops Reused</p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-blue-600">{data.summary.overallUtilizationRate}%</p>
          <p className="text-sm text-gray-500">Utilization Rate</p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-purple-600">${data.summary.estimatedSavings.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Est. Savings</p>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center bg-yellow-50 border-yellow-200">
          <p className="text-2xl font-bold text-yellow-700">{data.summary.totalDropsAvailable}</p>
          <p className="text-sm text-yellow-600">Available Drops</p>
        </Card>
        <Card className="text-center bg-red-50 border-red-200">
          <p className="text-2xl font-bold text-red-700">{data.summary.totalDropsWasted}</p>
          <p className="text-sm text-red-600">Wasted Drops</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-700">{data.summary.totalLengthSaved.toLocaleString()}"</p>
          <p className="text-sm text-gray-500">Length Saved</p>
        </Card>
        <Card className="text-center bg-red-50 border-red-200">
          <p className="text-2xl font-bold text-red-700">{data.summary.wastePercentage}%</p>
          <p className="text-sm text-red-600">Waste Rate</p>
        </Card>
      </div>

      {/* By Component */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Drop Utilization by Component</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Component</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Used</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Available</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Util %</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Savings</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.byComponent.map((comp, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{comp.part_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{comp.component_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{comp.drops_created}</td>
                  <td className="px-4 py-3 text-sm text-green-600 text-right font-medium">{comp.drops_used}</td>
                  <td className="px-4 py-3 text-sm text-yellow-600 text-right">{comp.drops_available}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      comp.utilization_rate >= 70 ? 'bg-green-100 text-green-800' :
                      comp.utilization_rate >= 40 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {comp.utilization_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-purple-600 text-right font-medium">${comp.estimated_savings.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent Drops */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Drops</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part #</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Length</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.recentDrops.map((drop, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{drop.part_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{drop.length}"</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      drop.status === 'available' ? 'bg-green-100 text-green-800' :
                      drop.status === 'used' ? 'bg-blue-100 text-blue-800' :
                      drop.status === 'reserved' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {drop.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{drop.order_number || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(drop.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Waste Tracking Report Component
function WasteReportView({ data }: { data: WasteReport }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="text-center bg-red-50 border-red-200">
          <p className="text-3xl font-bold text-red-700">{data.summary.totalWasteEvents}</p>
          <p className="text-sm text-red-600">Waste Events</p>
        </Card>
        <Card className="text-center bg-red-50 border-red-200">
          <p className="text-3xl font-bold text-red-700">{data.summary.totalWasteQuantity.toLocaleString()}</p>
          <p className="text-sm text-red-600">Total Quantity</p>
        </Card>
        <Card className="text-center bg-red-50 border-red-200">
          <p className="text-3xl font-bold text-red-700">${data.summary.totalWasteCost.toLocaleString()}</p>
          <p className="text-sm text-red-600">Total Cost</p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-gray-700">${data.summary.avgWasteCostPerDay.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Avg Cost/Day</p>
        </Card>
      </div>

      {/* Period Info */}
      <Card className="bg-gray-50">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            Reporting period: <strong>{data.summary.periodDays} days</strong>
            {data.summary.topWastedPart !== 'N/A' && (
              <> | Top wasted: <strong>{data.summary.topWastedPart}</strong></>
            )}
          </span>
        </div>
      </Card>

      {/* By Component Type */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Waste by Component Type</h3>
        {data.byComponentType.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No waste recorded for this period</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Component Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Events</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg/Event</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.byComponentType.map((comp, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{comp.component_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{comp.waste_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{comp.total_quantity.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right font-medium">${comp.total_cost.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">${comp.avg_cost_per_waste.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recent Waste */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Waste Items</h3>
        {data.recentWaste.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No waste recorded for this period</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.recentWaste.map((item, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.part_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity_wasted}</td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right font-medium">${item.total_cost.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.waste_reason || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(item.waste_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// Mock data for development
function getMockData(type: ReportType): ConsumptionReport | DropReport | WasteReport {
  if (type === 'consumption') {
    return {
      summary: {
        totalPartsConsumed: 1256,
        totalCost: 8945.67,
        uniquePartsUsed: 47,
        avgDailyConsumption: 41.87,
        topConsumedPart: '11134.0 - 3/4" Pile Brush',
        periodDays: 30,
      },
      byGroup: [
        {
          inventory_group: 'Hardware',
          total_consumed: 523,
          total_cost: 2345.67,
          items: [
            { part_number: '11153.0', description: 'Wheel Holder Bracket (Left)', quantity_consumed: 89, unit_price: 4.50, total_cost: 400.50, movement_count: 12 },
            { part_number: '11157.0', description: 'Wheel Guides x (2)', quantity_consumed: 156, unit_price: 2.25, total_cost: 351.00, movement_count: 18 },
          ],
        },
        {
          inventory_group: 'Extrusions',
          total_consumed: 345,
          total_cost: 4567.89,
          items: [
            { part_number: '11001.0', description: 'Housing Main - Black', quantity_consumed: 120, unit_price: 12.50, total_cost: 1500.00, movement_count: 15 },
          ],
        },
      ],
      trends: [
        { date: '2026-01-01', quantity: 45, cost: 320 },
        { date: '2026-01-02', quantity: 52, cost: 380 },
      ],
      topItems: [
        { part_number: '11134.0', description: '3/4" Pile Brush', quantity_consumed: 234, total_cost: 702.00 },
        { part_number: '11153.0', description: 'Wheel Holder Bracket (Left)', quantity_consumed: 189, total_cost: 850.50 },
        { part_number: '11001.0', description: 'Housing Main - Black', quantity_consumed: 156, total_cost: 1950.00 },
      ],
    };
  } else if (type === 'drops') {
    return {
      summary: {
        totalDropsCreated: 145,
        totalDropsUsed: 98,
        totalDropsAvailable: 32,
        totalDropsWasted: 15,
        overallUtilizationRate: 67.59,
        totalLengthSaved: 4567.5,
        estimatedSavings: 2283.75,
        wastePercentage: 10.34,
      },
      byComponent: [
        { part_number: '11001.0', description: 'Housing Main - Black', component_type: 'Housing', drops_created: 45, drops_used: 32, drops_available: 8, drops_wasted: 5, total_length_created: 1800, total_length_used: 1280, utilization_rate: 71.11, estimated_savings: 640.00 },
        { part_number: '11002.0', description: 'V-Track - Black', component_type: 'Track', drops_created: 38, drops_used: 28, drops_available: 6, drops_wasted: 4, total_length_created: 1520, total_length_used: 1120, utilization_rate: 73.68, estimated_savings: 560.00 },
        { part_number: '11003.0', description: 'Roller Tube - Black', component_type: 'Roller', drops_created: 62, drops_used: 38, drops_available: 18, drops_wasted: 6, total_length_created: 2480, total_length_used: 1520, utilization_rate: 61.29, estimated_savings: 760.00 },
      ],
      recentDrops: [
        { id: '1', part_number: '11001.0', length: 84, status: 'available', created_at: '2026-01-15T10:30:00Z', order_number: 'VT26010042' },
        { id: '2', part_number: '11002.0', length: 96, status: 'used', created_at: '2026-01-14T14:20:00Z', order_number: 'VT26010041' },
        { id: '3', part_number: '11003.0', length: 72, status: 'waste', created_at: '2026-01-13T09:15:00Z', order_number: 'VT26010040' },
      ],
    };
  } else {
    return {
      summary: {
        totalWasteEvents: 23,
        totalWasteQuantity: 156.5,
        totalWasteCost: 1234.56,
        avgWasteCostPerDay: 41.15,
        topWastedPart: '11003.0 - Roller Tube - Black',
        periodDays: 30,
      },
      byComponentType: [
        { component_type: 'Extrusion', waste_count: 15, total_quantity: 98.5, total_cost: 789.00, avg_cost_per_waste: 52.60, items: [] },
        { component_type: 'Hardware', waste_count: 5, total_quantity: 45, total_cost: 312.50, avg_cost_per_waste: 62.50, items: [] },
        { component_type: 'Fabric', waste_count: 3, total_quantity: 13, total_cost: 133.06, avg_cost_per_waste: 44.35, items: [] },
      ],
      trends: [
        { date: '2026-01-01', quantity: 5, cost: 45 },
        { date: '2026-01-05', quantity: 8, cost: 72 },
      ],
      recentWaste: [
        { part_number: '11003.0', description: 'Roller Tube - Black', quantity_wasted: 24, total_cost: 192.00, waste_reason: 'Drop too short', waste_date: '2026-01-15T10:30:00Z' },
        { part_number: '11134.0', description: '3/4" Pile Brush', quantity_wasted: 12, total_cost: 36.00, waste_reason: 'Damaged', waste_date: '2026-01-14T14:20:00Z' },
      ],
    };
  }
}
